/**
 * 🚧 GTM EVENTS STAGING - Migration in Progress
 *
 * This file contains new GTM event implementations that are being validated
 * before connecting to production GTM.
 *
 * WORKFLOW:
 * 1. Implement event here (push to stagingDataLayer with feature flag)
 * 2. Validate with tracking analyst (100% complete including edge cases)
 * 3. Move implementation to events.ts (connects to real GTM)
 * 4. Update import in eventTrackers.ts
 *
 * WHY: We implement against stagingDataLayer first to ensure we never ship partial
 * or inconsistent tracking to production. Events only connect to GTM when 100% ready.
 *
 * FEATURE FLAG: GTM_EVENTS_MIGRATION_LOGS_ACTIVATED
 * Set in runtime config to enable staging data layer in non-production environments.
 *
 * @see https://backmarket.atlassian.net/wiki/spaces/BOT/pages/6197674093/Pillar+1+Upgrade+our+GTM+implementation
 */

import type { RouteLocationNormalized } from 'vue-router'

import type { AddToCartParams, DataLayerEvent, EventData } from '../../../types'

import { pushStagingEventToStagingDataLayer } from './events.utils'

// ============================================================================
// 🚧 STAGING EVENTS
// Add new event implementations below.
// Use pushStagingEventToStagingDataLayer() instead of pushEventToDataLayer() during migration.
// ============================================================================

export function trackPageView(to: RouteLocationNormalized) {
  const event: DataLayerEvent = {
    event: 'page_view',
    page_info: {
      page_category: to.name,
      content_group: to.meta.tracking?.contentGroup ?? 'Unmapped',
    },
  }

  pushStagingEventToStagingDataLayer(event)
}

export function trackViewSearchResults({ searchTerm }: { searchTerm: string }) {
  const event = { event: 'view_search_results', search_term: searchTerm }

  pushStagingEventToStagingDataLayer(event)
}

export type SelectPromotionParams = {
  creativeName: string
  creativeSlot: string
}

export function trackSelectPromotion({
  creativeName,
  creativeSlot,
}: SelectPromotionParams): void {
  const event: DataLayerEvent = {
    event: 'select_promotion',
    ecommerce: {
      creative_name: creativeName,
      creative_slot: creativeSlot,
    },
  }

  pushStagingEventToStagingDataLayer(event)
}

export function trackViewCart(payload: EventData): void {
  const event: DataLayerEvent = { event: 'view_cart', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export function trackSelectItem(payload: EventData): void {
  const event: DataLayerEvent = { event: 'select_item', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export function trackViewItemList(payload: EventData): void {
  const event: DataLayerEvent = { event: 'view_item_list', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export function trackBeginCheckout(payload: EventData): void {
  const event: DataLayerEvent = { event: 'begin_checkout', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export function trackAddPaymentInfo(payload: EventData): void {
  const event: DataLayerEvent = { event: 'add_payment_info', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export function trackAddShippingInfo(payload: EventData): void {
  const event: DataLayerEvent = { event: 'add_shipping_info', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export type ViewPromotionParams = {
  creativeName: string
  creativeSlot: string
}

export function trackViewPromotion({
  creativeName,
  creativeSlot,
}: ViewPromotionParams): void {
  const event: DataLayerEvent = {
    event: 'view_promotion',
    ecommerce: {
      creative_name: creativeName,
      creative_slot: creativeSlot,
    },
  }

  pushStagingEventToStagingDataLayer(event)
}

export type ViewItemParams = {
  currency: string
  value: number
  item: {
    itemId: string
    itemUuid: string
    itemName: string
    affiliation: string
    discount: number
    itemBrand: string
    itemCategory: string
    itemVariant: string
    itemWarrantyDuration: string
    itemIsTradeInEligible: boolean
    locationId: string
    price: number
  }
}

export function trackViewItem({ currency, value, item }: ViewItemParams): void {
  const event: DataLayerEvent = {
    event: 'view_item',
    ecommerce: {
      currency,
      value,
      items: [
        {
          item_id: item.itemId,
          item_uuid: item.itemUuid,
          item_name: item.itemName,
          affiliation: item.affiliation,
          discount: item.discount,
          index: 0,
          item_brand: item.itemBrand,
          item_category: item.itemCategory,
          item_variant: item.itemVariant,
          item_warranty_duration: item.itemWarrantyDuration,
          item_is_trade_in_eligible: item.itemIsTradeInEligible,
          location_id: item.locationId,
          price: item.price,
          quantity: 1,
          google_business_vertical: 'retail',
          // TODO [BB-25.1]: item_category2..item_category5 — only L1 category on product API
          // TODO [BB-25.2]: item_variant2..item_variant15 — only grade variant exposed
          // TODO [BB-25.4]: item_is_favorited, item_days_since_favorited, item_price_when_favorited, item_favorited_location — favorites state not wired into PDP tracking
          // TODO [BB-25.6]: item_has_free_return, item_free_return_window — only shipping.free.delayInHours exposed
          // TODO [BB-25.7]: item_finance_available, item_finance_provider — paymentMethods not mapped to GA4 provider names
          // TODO [BB-25.10]: item_list_id, item_list_name — only freeform sessionStorage source persists click context
        },
      ],
    },
  }

  pushStagingEventToStagingDataLayer(event)
}

export function trackPurchase(payload: EventData): void {
  const event: DataLayerEvent = { event: 'purchase', ...payload }

  pushStagingEventToStagingDataLayer(event)
}

export type AddToFavoriteParams = {
  currency: string
  value: number
  item: ViewItemParams['item'] & {
    itemPriceWhenFavorited: number
    itemFavoritedLocation: string
  }
}

export function trackAddToFavorite({
  currency,
  value,
  item,
}: AddToFavoriteParams): void {
  const event: DataLayerEvent = {
    event: 'add_to_wishlist',
    ecommerce: {
      currency,
      value,
      items: [
        {
          item_id: item.itemId,
          item_uuid: item.itemUuid,
          item_name: item.itemName,
          affiliation: item.affiliation,
          discount: item.discount,
          index: 0,
          item_brand: item.itemBrand,
          item_category: item.itemCategory,
          item_variant: item.itemVariant,
          item_warranty_duration: item.itemWarrantyDuration,
          item_is_trade_in_eligible: item.itemIsTradeInEligible,
          location_id: item.locationId,
          price: item.price,
          quantity: 1,
          google_business_vertical: 'retail',
          item_is_favorited: true,
          item_days_since_favorited: 0,
          item_price_when_favorited: item.itemPriceWhenFavorited,
          item_favorited_location: item.itemFavoritedLocation,
          // TODO [BB-26.1]: item_category2..item_category5 — only L1 category on product API
          // TODO [BB-26.2]: item_variant2..item_variant15 — only grade variant exposed
          // TODO [BB-26.5]: item_has_free_return, item_free_return_window — only shipping.free.delayInHours exposed
          // TODO [BB-26.6]: item_finance_available, item_finance_provider — paymentMethods not mapped to GA4 provider names
          // TODO [BB-26.9]: item_list_id, item_list_name — only freeform sessionStorage source persists click context
        },
      ],
    },
  }

  pushStagingEventToStagingDataLayer(event)
}

export function trackAddToCart({
  hasTradeIn,
  zone,
  currency,
  value,
  items,
}: AddToCartParams): void {
  const event: DataLayerEvent = {
    event: 'add_to_cart',
    add_to_cart_location: zone,
    has_trade_in: hasTradeIn,
    ecommerce: {
      currency,
      value,
      items,
    },
  }

  pushStagingEventToStagingDataLayer(event)
}

export function trackRemoveFromCart(payload: EventData): void {
  const event: DataLayerEvent = { event: 'remove_from_cart', ...payload }

  pushStagingEventToStagingDataLayer(event)
}
